import React from 'react';
import { captureException } from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // ChunkLoadError: Nach einem Deployment existieren alte Lazy-Chunks nicht
    // mehr - einmaliges Neuladen holt die neue Version (Guard gegen Reload-Loop).
    const isChunkError = error?.name === 'ChunkLoadError'
      || /Loading (CSS )?chunk [^ ]+ failed/i.test(error?.message || '');
    if (isChunkError) {
      try {
        const alreadyReloaded = sessionStorage.getItem('chunk-error-reloaded');
        if (!alreadyReloaded) {
          sessionStorage.setItem('chunk-error-reloaded', String(Date.now()));
          window.location.reload();
          return;
        }
      } catch (storageError) {
        // sessionStorage nicht verfügbar - normale Fehlerseite zeigen
      }
    }

    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Render-Fehler erreichen window.onerror NICHT - ohne explizites Capture
    // sieht Sentry sie nie ("Entwickler wurden benachrichtigt" wäre gelogen)
    try {
      captureException(error, {
        contexts: {
          react: { componentStack: errorInfo?.componentStack },
        },
        tags: { errorBoundary: 'true' },
      });
    } catch (sentryError) {
      console.warn('Sentry-Meldung fehlgeschlagen:', sentryError);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-surface rounded-xl shadow-token-xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-danger-soft rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-danger" />
            </div>
            
            <h1 className="text-2xl font-bold text-foreground mb-4">
              Oops! Etwas ist schief gelaufen
            </h1>
            
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Tut mir leid, hier ist ein unerwarteter Fehler aufgetreten.
              Unsere Entwickler wurden automatisch benachrichtigt.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-xl font-medium hover:bg-primary-hover transition-colors duration-fast flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Seite neu laden
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="w-full bg-surface-raised text-foreground-2 py-3 px-4 rounded-xl font-medium hover:bg-foreground/10 transition-colors duration-fast flex items-center justify-center gap-2"
              >
                <Home className="w-4 h-4" />
                Zur Startseite
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Technische Details (nur im Development-Modus)
                </summary>
                <div className="mt-2 p-4 bg-surface-raised rounded-lg text-xs font-mono text-muted-foreground overflow-auto max-h-40">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.toString()}
                  </div>
                  <div>
                    <strong>Stack Trace:</strong>
                    <pre className="whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                  </div>
                </div>
              </details>
            )}
            
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Wenn das Problem weiterhin besteht, schreib mir an{' '}
                <a 
                  href="mailto:support@eachotter.de" 
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  support@eachotter.de
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
